import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Search, Trophy, MessageCircle, Users, Activity, Calendar } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { usePageSeo } from '../hooks/usePageSeo';
import { absoluteUrl } from '../utils/seo';
import ForumCardWrapper from './ForumCardWrapper';
import type { ForumBase } from '../types/forum';
interface Forum {
  id: string;
  forum_id: string;
  name: string;
  url: string;
  category: string;
  topics_count: number;
  posts_count: number;
  members_count: number;
  founded_year: number;
  description: string;
  logo_url: string;
  is_active: boolean;
  promo_section_url?: string | null;
  forum_admins?: { name: string; profile_url: string }[] | null;
  forum_controllers?: { name: string; profile_url: string }[] | null;
  forum_admin_name?: string | null;
  forum_admin_profile_url?: string | null;
}

interface Props {
  darkMode: boolean;
}

const InactiveForums: React.FC<Props> = ({ darkMode }) => {
  const [forums, setForums] = useState<Forum[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState<keyof Forum>('name');

  useEffect(() => {
    fetchInactiveForums();
  }, []);

  usePageSeo({
    title: 'Forum Mezarlığı - Kapanan Forum Siteleri',
    description:
      'Kapanan ve artık aktif olmayan Türk forum siteleri listesi. Eski forum sitelerinin arşivi ve son istatistikleri.',
    keywords: 'kapanan forumlar, eski forum siteleri, forum arşivi, inaktif forumlar, forum mezarlığı',
    canonical: absoluteUrl('/inactive'),
  });

  const fetchInactiveForums = async () => {
    try {
      const { data, error } = await supabase
        .from('forums')
        .select('*')
        .eq('is_active', false)
        .order('name');

      if (error) throw error;
      setForums(data || []);
    } catch (error) {
      console.error('Error fetching inactive forums:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateScore = (forum: Forum): number => {
    const topicsScore = (forum.topics_count || 0) * 5;
    const postsScore = (forum.posts_count || 0) * 2;
    const membersScore = (forum.members_count || 0) * 10;
    return Math.round((topicsScore + postsScore + membersScore) / forum.founded_year);
  };

  const filteredForums = forums
    .filter(forum =>
      forum.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      forum.category?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'score') {
        return calculateScore(b) - calculateScore(a);
      }
      if (sortBy === 'founded_year') {
        return (a.founded_year || 0) - (b.founded_year || 0);
      }
      const valueA = a[sortBy] ?? 0;
      const valueB = b[sortBy] ?? 0;
      return (valueB as number) - (valueA as number);
    });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="container mx-auto px-3 sm:px-4 py-6 sm:py-8 max-w-7xl">
        <Link
          to="/"
          className={`inline-flex items-center gap-2 mb-6 ${
            darkMode ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <ArrowLeft size={20} />
          Geri Dön
        </Link>

        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Forum Mezarlığı</h1>
          <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Artık aktif olmayan {forums.length} forum listeleniyor
          </p>
        </div>

        <div className="mb-6 flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="relative max-w-md w-full">
            <Search
              className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                darkMode ? 'text-gray-500' : 'text-gray-400'
              }`}
              size={20}
            />
            <input
              type="text"
              placeholder="Forum ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-lg border ${
                darkMode
                  ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                  : 'border-gray-200'
              } focus:outline-none focus:ring-2 focus:ring-indigo-500`}
            />
          </div>

          <div className="flex gap-2">
            <button
              onClick={() => setSortBy('score')}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                sortBy === 'score'
                  ? darkMode ? 'bg-indigo-500 text-white' : 'bg-indigo-600 text-white'
                  : darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Trophy size={18} />
              Skor
            </button>
            <button
              onClick={() => setSortBy('topics_count')}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                sortBy === 'topics_count'
                  ? darkMode ? 'bg-indigo-500 text-white' : 'bg-indigo-600 text-white'
                  : darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <MessageCircle size={18} />
              Konu
            </button>
            <button
              onClick={() => setSortBy('posts_count')}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                sortBy === 'posts_count'
                  ? darkMode ? 'bg-indigo-500 text-white' : 'bg-indigo-600 text-white'
                  : darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Activity size={18} />
              İleti
            </button>
            <button
              onClick={() => setSortBy('members_count')}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                sortBy === 'members_count'
                  ? darkMode ? 'bg-indigo-500 text-white' : 'bg-indigo-600 text-white'
                  : darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Users size={18} />
              Üye
            </button>
          </div>
        </div>

        <div className="grid gap-6">
          {filteredForums.map((forum) => (
            <ForumCardWrapper
              key={forum.id}
              to={`/forum/${forum.forum_id}`}
              forumId={forum.forum_id}
              darkMode={darkMode}
              adminMeta={forum as ForumBase}
              className={`block ${
                darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-50'
              } rounded-xl p-6 shadow-sm transition-all duration-200`}
            >
              <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-4">
                    {forum.logo_url && (
                      <img src={forum.logo_url} alt={forum.name} className="w-[150px] h-[45px] object-contain" />
                    )}
                    <div>
                      <h2 className="text-xl font-semibold">{forum.name}</h2>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span className={darkMode ? 'text-gray-400' : ''}>{forum.category}</span>
                        <span className={darkMode ? 'text-gray-600' : ''}>•</span>
                        <div className="flex items-center gap-1">
                          <Calendar size={14} className={darkMode ? 'text-gray-400' : ''} />
                          <span className={darkMode ? 'text-gray-400' : ''}>{forum.founded_year}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="flex items-center gap-3">
                      <Trophy className="text-amber-500 shrink-0" size={20} />
                      <div>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Son Skor</p>
                        <p className={`font-semibold ${darkMode ? 'text-gray-200' : ''}`}>
                          {calculateScore(forum).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <MessageCircle className="text-purple-500 shrink-0" size={20} />
                      <div>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Son Konu Sayısı</p>
                        <p className={`font-semibold ${darkMode ? 'text-gray-200' : ''}`}>
                          {forum.topics_count.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Activity className="text-green-500 shrink-0" size={20} />
                      <div>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Son İleti Sayısı</p>
                        <p className={`font-semibold ${darkMode ? 'text-gray-200' : ''}`}>
                          {forum.posts_count.toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Users className="text-blue-500 shrink-0" size={20} />
                      <div>
                        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Son Üye Sayısı</p>
                        <p className={`font-semibold ${darkMode ? 'text-gray-200' : ''}`}>
                          {forum.members_count.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ForumCardWrapper>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InactiveForums;